type LenisOptions = {
    duration?: number;
    easing?: (t: number) => number;
    syncTouch?: boolean;
    touchMultiplier?: number;
    wheelMultiplier?: number;
};

type LenisInstance = {
    raf: (time: number) => void;
    start: () => void;
    stop: () => void;
    on: (event: string, cb: (...args: unknown[]) => void) => void;
    scrollTo: (
        target: number,
        opts?: { immediate?: boolean; force?: boolean },
    ) => void;
};

type LenisCtor = new (opts?: LenisOptions) => LenisInstance;

export class SmoothScrollManager {
    static init(): void {
        const Lenis = (window as unknown as { Lenis?: LenisCtor }).Lenis;
        if (!Lenis) {
            console.warn('[SmoothScrollManager] window.Lenis not found — lenis.js may have failed to load or returned 404');
            return;
        }

        const isTouchDevice = window.matchMedia(
            "(hover: none) and (pointer: coarse)",
        ).matches;

        let lenis: LenisInstance;
        try {
            lenis = new Lenis({
                duration: isTouchDevice ? 1.0 : 0.9,
                easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
                syncTouch: false,
                touchMultiplier: isTouchDevice ? 0.75 : 1,
                wheelMultiplier: 1,
            });
        } catch (e) {
            console.error('[SmoothScrollManager] Lenis instantiation failed:', e);
            return;
        }

        (window as unknown as { lenis: LenisInstance }).lenis = lenis;

        // scrollRestoration=manual 보강: Lenis 내부 상태까지 0 으로 동기화.
        // window.scrollTo 단독은 Lenis animatedScroll/targetScroll 와 어긋남
        // → lenis.scrollTo immediate. ticker 등록(아래) 전에 호출해 첫 raf 가 0 에서 출발.
        if (history.scrollRestoration === "manual") {
            lenis.scrollTo(0, { immediate: true, force: true });
        }

        type GsapWindow = {
            gsap?: {
                ticker: {
                    add: (cb: (time: number) => void) => void;
                    lagSmoothing: (threshold?: number, adjustedLag?: number) => void;
                };
            };
            ScrollTrigger?: { update: () => void };
        };
        const w = window as unknown as GsapWindow;

        if (w.gsap) {
            if (w.ScrollTrigger) {
                lenis.on('scroll', w.ScrollTrigger.update);
            }
            w.gsap.ticker.add((time: number) => {
                lenis.raf(time * 1000);
            });
            // 임계 500ms — 0.5초 이상 프레임 누락 시 즉시 보정(기존 1000ms는 너무 늦음).
            w.gsap.ticker.lagSmoothing(500, 16);

            // 스크롤 중 body.is-scrolling 마커 — CSS hover transition이 Lenis rAF와
            // GPU compositing 큐를 경합하는 문제 해소. 스크롤 정지 150ms 후 제거해
            // 정지 상태 hover 효과(이미지 zoom, 텍스트 색)는 정상 작동.
            let _scrollEndTimer: ReturnType<typeof setTimeout> | undefined;
            lenis.on('scroll', () => {
                document.body.classList.add('is-scrolling');
                clearTimeout(_scrollEndTimer);
                _scrollEndTimer = setTimeout(() => {
                    document.body.classList.remove('is-scrolling');
                }, 150);
            });
        } else {
            const raf = (time: number) => {
                lenis.raf(time);
                requestAnimationFrame(raf);
            };
            requestAnimationFrame(raf);
        }
    }
}
